import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import ZohoLogo from '../../../components/ZohoLogo';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import Tooltip from '../../../components/ui/Tooltip';
import StatusBadge from './StatusBadge';
import AvailabilityLabel from './AvailabilityLabel';
import PrioritizeButton from './PrioritizeButton';
import { cn } from '../../../utils/cn';
import { getCandidateProfilePath } from '../../../lib/utils/candidateRoutes';
import { getCandidatesListPathFromSearchParams } from '../../../lib/utils/candidateListRoutes';
import { Checkbox } from '../../../components/ui/Checkbox';
import { planCanPrioritize } from '../../../lib/utils/planEntitlements';

const CandidateCard = ({
  candidate,
  onTogglePriority,
  onDeleteCandidate,
  userPlan,
  isRestricted,
  isSelected = false,
  onToggleSelect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Normalize plan to lowercase for comparison
  const plan = (userPlan ?? '').toLowerCase();
  
  // Feature availability based on plan
  const canPrioritize = planCanPrioritize(plan);

  const handleViewProfile = () => {
    navigate(getCandidateProfilePath(candidate?.id), {
      state: { returnTo: getCandidatesListPathFromSearchParams(searchParams) },
    });
  };

  const handleDeleteClick = () => setShowDeleteConfirm(true);
  const handleDeleteConfirm = () => {
    onDeleteCandidate?.(candidate?.id);
    setShowDeleteConfirm(false);
  };

  const hasPhone = Boolean(candidate?.phone?.trim());

  // Generate initials from full name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div
      className={cn(
        'bg-card border border-border rounded-lg p-4 hover:bg-muted hover:shadow-md transition-all',
        !isRestricted && 'cursor-pointer'
      )}
      onClick={!isRestricted ? handleViewProfile : undefined}
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className="pt-1 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            id={`select-candidate-mobile-${candidate?.id}`}
            checked={isSelected}
            onChange={() => onToggleSelect?.(candidate?.id)}
            aria-label={`Select ${candidate?.fullName || 'candidate'}`}
          />
        </div>
        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
          {candidate?.profileImage ? (
            <Image
              src={candidate?.profileImage}
              alt={candidate?.profileImageAlt}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
              {getInitials(candidate?.fullName)}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-medium text-foreground truncate">
              {candidate?.fullName}
            </h3>
            {candidate?.isNew && (
              <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                New
              </span>
            )}
            {candidate?.isFromZoho && (
              <Tooltip content="Imported from Zoho Recruit" align="start" size="sm">
                <span className="inline-flex items-center justify-center flex-shrink-0">
                  <ZohoLogo size={18} />
                </span>
              </Tooltip>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {candidate?.currentRole}
          </p>
        </div>
      </div>
      <div className="space-y-2 mb-4">
        <div>
          <p className="text-xs text-muted-foreground">Company</p>
          <p className="text-sm text-foreground">
            {candidate?.companyName}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">Status</p>
            <div className="inline-flex items-center gap-1.5">
              <StatusBadge status={candidate?.status} />
              {candidate?.isManualOpenToWork && (
                <Tooltip content="Status set manually" align="start" size="sm">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted border border-border text-muted-foreground">
                    <Icon name="Pencil" size={11} />
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Last Check</p>
            <p className="text-sm text-foreground">
              {candidate?.lastCheckDate}
            </p>
          </div>
        </div>

        <div>
          <p className="text-xs text-muted-foreground mb-1.5 text-center">Contact</p>
          <div className="flex flex-wrap justify-center gap-2">
            <AvailabilityLabel type="phone" available={hasPhone} />
            <AvailabilityLabel type="linkedin" available={!candidate?.isInvalidLink} />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
        {!isRestricted && canPrioritize && (
          <PrioritizeButton
            isPrioritized={candidate?.isPrioritized}
            hasPhone={hasPhone}
            onClick={() => onTogglePriority(candidate?.id)}
            showTooltip={false}
            fullWidth
          />
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDeleteClick}
          iconName="Trash2"
          fullWidth
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          {t('common.delete')}
        </Button>
      </div>
      {createPortal(
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteConfirm}
          title={t('candidates.deleteCandidateTitle')}
          message={t('candidates.deleteCandidateMessage', { name: candidate?.fullName })}
          confirmText={t('common.delete')}
          cancelText={t('common.cancel')}
        />,
        document.body
      )}
    </div>
  );
};

export default CandidateCard;